/**
 * Direct messages.
 *
 * The frame is built once and re-pointed, never rebuilt - swapping the whole
 * pane on every chat switch is what made the window flash. Opening Chat with
 * nobody in mind lands you on your most recent conversation.
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, endpoints, fileUrl, socket } from '@/lib/api';
import type { Message, PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { rememberChat } from '@/lib/lastChat';
import { openPalette } from '@/lib/palette';
import { TypingLine } from '@/components/TypingLine';
import { patchMessage } from '@/lib/optimistic';
import { useSession } from '@/stores/session';
import { Avatar } from '@/components/Avatar';
import { ChatLog, Composer } from '@/components/chat';
import { UserName, openUserCard, absolute } from '@/components/user';
import { InviteToParty, useUserMenu } from '@/components/userMenu';
import * as Icons from '@/icons';
import { IconButton, useToast } from '@/components/ui';
import { Tooltip } from '@/components/Tooltip';
import { FramedAvatar } from '@/components/FramedAvatar';
import { Tip } from '@/components/HoverCard';
import { Icon } from '@/icons/Icon';
import { gameIconArt } from '@/lib/gameArt';
import { useNarrow } from '@/components/TabBar';
import { useMinWidth, CHAT_DETAILS_INLINE } from '@/lib/viewport';
import { useSplitter } from '@/lib/splitter';
import { Grip } from '@/components/Grip';
import { Lightbox } from '@/components/Lightbox';
import { PanelFold } from '@/components/PanelFold';
import { ChatTools } from '@/components/ChatTools';
import { MessageRequests } from '@/components/MessageRequests';
import { BOT_USERNAME, BotComposer, BotThread, botUser, type Announcement } from '@/components/PugoBot';
import { GroupRow, GroupPanel, GroupHeader, NewGroup, useLeaveGroup } from '@/components/GroupChat';
import { EmojiText } from '@/components/EmojiText';
import { CallBar, CallButton, dmRoom, groupRoom } from '@/components/ChatCall';

type Thread = { user: PublicUser; last: Message | null; unread: number };

type DmPayload = {
  channel: string;
  other: PublicUser;
  messages: Message[];
  /** True when there is history behind this page. */
  more: boolean;
};

/* One shared empty list, so a group with no messages is not a new array
   every render and a fresh dependency for everything downstream. */
const EMPTY_MESSAGES: Message[] = [];

export function ChatView({ username, groupId }: { username?: string; groupId?: string }) {
  const [filter, setFilter] = useState('');
  // The details panel is remembered, because whether you want it is a habit
  // rather than a per-conversation decision.
  const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem('pogu.chatPanel') !== 'off');
  useEffect(() => { localStorage.setItem('pogu.chatPanel', panelOpen ? 'on' : 'off'); }, [panelOpen]);

  /*
   * The details panel yields space first.
   *
   * Laid out beside the conversation at every desktop width, a 320px details
   * panel next to a 320px list and the sidebar left the conversation 156px wide
   * at 1092px - messages wrapped at 54px and the composer collapsed to nothing.
   * Now it only sits inline when the conversation can still be ~560px with it
   * open; below that it opens as a drawer over the conversation instead.
   *
   * The saved on/off preference belongs to the inline panel. The drawer starts
   * closed every time, or a remembered "on" would throw a panel over the
   * messages on every visit to a narrower window.
   */
  const detailsInline = useMinWidth(CHAT_DETAILS_INLINE);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const detailsOpen = detailsInline ? panelOpen : drawerOpen;
  const toggleDetails = () => (detailsInline ? setPanelOpen(!panelOpen) : setDrawerOpen((v) => !v));
  const [messages, setMessages] = useState<Message[]>([]);
  /* Whether there is history behind what is loaded, and a fetch in flight. */
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  /* The new-group dialog. */
  const [making, setMaking] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);

  /**
   * Conversations you have closed.
   *
   * Hiding a thread is a view preference, not a deletion - the messages stay
   * on the server, searching the person brings the thread back, and a new
   * message from them reopens it on its own.
   */
  const [closed, setClosed] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('pogu.closedChats') ?? '[]') as string[]; }
    catch { return []; }
  });

  function closeThread(name: string) {
    const next = [...new Set([...closed, name])];
    setClosed(next);
    try { localStorage.setItem('pogu.closedChats', JSON.stringify(next)); } catch { /* storage disabled */ }
    /* Do not let the Chat button come back to something you closed. */
    rememberChat(null);
    if (username === name) go('chat');
  }

  function reopenThread(name: string) {
    const next = closed.filter((c) => c !== name);
    setClosed(next);
    try { localStorage.setItem('pogu.closedChats', JSON.stringify(next)); } catch { /* storage disabled */ }
  }
  const queryClient = useQueryClient();
  const narrow = useNarrow();
  const toast = useToast();
  const me = useSession((s) => s.me);

  const threads = useQuery({
    queryKey: ['dms'],
    queryFn: () => api.get<{ threads: { user: PublicUser; last: Message; unread: number }[] }>('/api/dms'),
    /*
     * Keep showing the list you had while the next one is fetched.
     *
     * Without this the data goes undefined for the moment a refetch is in
     * flight - so sending a message emptied the sidebar, dropped every last
     * message and re-sorted it, which reads as people vanishing.
     */
    placeholderData: (previous) => previous,
    staleTime: 10_000
  });
  /* Group conversations, listed alongside the people. */
  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: endpoints.groups,
    placeholderData: (previous) => previous,
    staleTime: 10_000
  });

  /* The one that is open, if it is a group. */
  const groupLog = useQuery({
    queryKey: ['group-log', groupId],
    queryFn: () => endpoints.groupLog(groupId!),
    enabled: !!groupId,
    placeholderData: (previous) => previous
  });

  const friends = useQuery({
    queryKey: ['friends'],
    queryFn: endpoints.friends,
    placeholderData: (previous) => previous,
    staleTime: 30_000
  });

  const convo = useQuery<DmPayload>({
    queryKey: ['dm', username],
    queryFn: () => api.get<DmPayload>(
      '/api/messages/dm/' + encodeURIComponent(username!)
    ),
    enabled: !!username
  });

  /**
   * One effect owns the message list.
   *
   * Two - one seeding from the query, one clearing on switch - raced: coming
   * back to a conversation already in cache re-ran only the clear, because the
   * query data had not changed, and the chat opened empty.
   */
  useEffect(() => {
    setMessages(convo.data?.messages ?? []);
    setHasOlder(!!convo.data?.more);
    setReplyTo(null);
  }, [username, convo.data]);

  /**
   * The page before the oldest message on screen.
   *
   * Prepended rather than refetched, so the conversation you are reading is
   * not rebuilt around you - ChatLog holds your scroll position while it
   * happens.
   */
  async function loadOlder() {
    const oldest = messages[0];
    if (!oldest || !username || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const res = await api.get<{ messages: Message[]; more: boolean }>(
        '/api/messages/dm/' + encodeURIComponent(username) + '?before=' + oldest.id
      );
      setMessages((list) => [...res.messages, ...list]);
      setHasOlder(res.more);
    } catch {
      // Nothing to say - the marker stays and scrolling up tries again.
    } finally {
      setLoadingOlder(false);
    }
  }

  useEffect(() => {
    const off = [
      socket.on('message:new', (msg: Message) => {
        if (msg.scope !== 'dm') return;
        void queryClient.invalidateQueries({ queryKey: ['dms'] });
        const other = convo.data?.other;
        if (!other || !me) return;
        const ids = msg.channelId.slice(3).split('|');
        if (!ids.includes(other.id) || !ids.includes(me.id)) return;
        setMessages((list) => [...list, msg]);
        socket.send('read', { channelId: msg.channelId });
      }),
      socket.on('message:reaction', (d: { messageId: string; reactions: Record<string, string[]> }) =>
        setMessages((l) => l.map((m) => (m.id === d.messageId ? { ...m, reactions: d.reactions } : m)))),
      socket.on('message:edited', (msg: Message) =>
        setMessages((l) => l.map((m) => (m.id === msg.id ? msg : m)))),
      socket.on('message:deleted', (d: { messageId: string }) =>
        setMessages((l) => l.filter((m) => m.id !== d.messageId))),
      /* Presence is patched into every cache by the shell - see App. Nothing
         to refetch here. */
    ];
    return () => off.forEach((fn) => fn());
  }, [queryClient, convo.data?.other, me]);

  /*
   * Remember where you are, so the Chat button can come straight back.
   *
   * Written on every change of conversation rather than on leaving, because
   * there is no reliable moment of leaving - the address can change under a
   * closing window, a reload, or a link from anywhere else in the app.
   */
  useEffect(() => {
    if (groupId) rememberChat('chat/g/' + groupId);
    else if (username) rememberChat('chat/' + username);
  }, [username, groupId]);

  // Opening Chat with nobody in mind goes to where you left off - the newest
  // conversation, or one with something waiting in it.
  const autoPicked = useRef(false);

  useEffect(() => {
    /*
     * A group is a chosen conversation too.
     *
     * This used to bail only on a username, so opening a group - which sets
     * groupId and leaves username undefined - looked exactly like arriving
     * at Chat with nobody in mind. The auto-pick then replaced the address
     * with the newest direct message, and the group you had just clicked
     * vanished in the same frame it appeared.
     */
    if (username || groupId) { autoPicked.current = false; return; }
    // On a phone the list is the screen. Opening the newest conversation for
    // you means the Chat tab never shows a list, only whoever spoke last.
    if (narrow) return;
    // Once per arrival. Without the guard, anything that re-ran this effect
    // sent you back to the top conversation mid-scroll.
    if (autoPicked.current || !threads.data?.threads.length) return;
    /*
     * Never a conversation you have just closed.
     *
     * Closing the one you were in leaves nothing selected, which brought this
     * straight back to the top of the list - and the top was the one you had
     * closed. It vanished and reappeared in the same breath.
     */
    const open = threads.data.threads.filter((t) => !closed.includes(t.user.username));
    if (!open.length) return;
    const unread = open.filter((t) => t.unread);
    const pick = (unread.length ? unread : open)
      .slice()
      .sort((a, b) => (b.last?.createdAt ?? 0) - (a.last?.createdAt ?? 0))[0];
    if (pick) { autoPicked.current = true; location.replace('#/chat/' + pick.user.username); }
  }, [username, groupId, threads.data, closed]);

  const list = useMemo<Thread[]>(() => {
    const byId = new Map<string, Thread>();
    for (const row of friends.data?.friends ?? []) {
      byId.set(row.user.id, { user: row.user, last: null, unread: 0 });
    }
    for (const t of threads.data?.threads ?? []) {
      byId.set(t.user.id, { user: t.user, last: t.last, unread: t.unread ?? 0 });
    }
    let entries = [...byId.values()];
    const q = filter.trim().toLowerCase();
    if (q) {
      entries = entries.filter((e) =>
        (e.user.username + ' ' + e.user.displayName).toLowerCase().includes(q));
    }

    // A closed thread stays hidden until you search for the person, open it
    // from elsewhere, or they message you.
    entries = entries.filter((e) =>
      !closed.includes(e.user.username)
      || !!q
      || e.user.username === username
      || e.unread > 0);

    // Newest first, full stop. Unread used to win, which floated a message
    // from Tuesday above the conversation you are having right now.
    return entries.sort((a, b) => {
      const at = a.last?.createdAt ?? 0;
      const bt = b.last?.createdAt ?? 0;
      if (at !== bt) return bt - at;
      // Never spoken to either of them: alphabetical beats arbitrary.
      return a.user.displayName.localeCompare(b.user.displayName);
    });
  }, [threads.data, friends.data, filter, closed, username]);

  // Switching chats should open the new one immediately and fill in, the way a
  // chat app does - not blank out to 'pick a conversation' for a beat. The
  // sidebar already knows who this is, so the frame is drawn from that while
  // the conversation itself is still in flight.
  /* Groups match the same search box the people do. */
  const myGroups = useMemo(() => {
    const all = groups.data?.groups ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter((g) =>
      g.name.toLowerCase().includes(q)
      || g.members.some((m) => m.user.displayName.toLowerCase().includes(q)));
  }, [groups.data, filter]);

  const openGroup = groupId ? (groupLog.data?.group ?? null) : null;

  /*
   * The group log lives in the cache, not in state beside it.
   *
   * It used to be copied into local state on every fetch, which quietly broke
   * every optimistic action in a group: pinning, reacting and editing all
   * write to the message caches, and a copy held in state is not one of them.
   * So a pin in a direct message moved at once and the same pin in a group sat
   * there until the socket echoed it back - the bug that looked like pinning
   * simply not working.
   */
  const groupMessages = groupLog.data?.messages ?? EMPTY_MESSAGES;

  /** Rewrites the open group's log in place. */
  const patchGroupLog = useCallback((edit: (list: Message[]) => Message[]) => {
    if (!groupId) return;
    queryClient.setQueryData<{ messages: Message[] }>(['group-log', groupId], (old) =>
      old ? { ...old, messages: edit(old.messages) } : old);
  }, [groupId, queryClient]);

  useEffect(() => { setReplyTo(null); }, [groupId]);

  /*
   * Live updates for the open group.
   *
   * The same four events the DM log listens for. Matched on channelId rather
   * than scope, because the server addresses a group by its channel and that
   * is the only thing both ends agree on.
   */
  useEffect(() => {
    if (!groupId) return;
    const channelId = 'gc:' + groupId;

    const off = [
      socket.on('message:new', (msg: Message) => {
        void queryClient.invalidateQueries({ queryKey: ['groups'] });
        if (msg.channelId !== channelId) return;
        patchGroupLog((list) => (list.some((m) => m.id === msg.id) ? list : [...list, msg]));
        socket.send('read', { channelId });
      }),
      socket.on('message:edited', (msg: Message) => {
        if (msg.channelId !== channelId) return;
        patchGroupLog((l) => l.map((m) => (m.id === msg.id ? msg : m)));
      }),
      socket.on('message:deleted', (d: { messageId: string; channelId: string }) => {
        if (d.channelId !== channelId) return;
        patchGroupLog((l) => l.filter((m) => m.id !== d.messageId));
      }),
      socket.on('message:reaction', (d: { messageId: string; channelId: string; reactions: Record<string, string[]> }) => {
        if (d.channelId !== channelId) return;
        patchGroupLog((l) => l.map((m) => (m.id === d.messageId ? { ...m, reactions: d.reactions } : m)));
      }),
      // Somebody renamed it, or the roster changed.
      socket.on('group:updated', (d: { group: { id: string } }) => {
        if (d.group?.id !== groupId) return;
        void queryClient.invalidateQueries({ queryKey: ['group-log', groupId] });
      }),
      // Removed from it, or it closed under you.
      socket.on('group:left', (d: { groupId: string }) => {
        void queryClient.invalidateQueries({ queryKey: ['groups'] });
        if (d.groupId === groupId) { rememberChat(null); go('chat'); }
      })
    ];
    return () => off.forEach((fn) => fn());
  }, [groupId, queryClient, patchGroupLog]);

  const { leave, dialog: leaveDialog } = useLeaveGroup();

  const known = username ? list.find((t) => t.user.username === username)?.user : undefined;
  const other = convo.data?.other ?? known;
  const loading = !!username && convo.isPending;

  /*
   * One list, most recent first.
   *
   * Splitting it by who happens to be online pushed the conversation you
   * were in the middle of below somebody you have not spoken to in a month,
   * purely because they were logged in. A green dot on the row says who is
   * about; the order should say what is happening.
   */
  const ordered = list;

  /*
   * Whichever conversation is actually open.
   *
   * The pinned bar and the Pinned panel used to read the direct-message list
   * unconditionally, so in a group they showed a pin from whoever you had
   * spoken to last - or nothing at all.
   */
  const shown = groupId ? groupMessages : messages;

  /*
   * Unread everywhere except here.
   *
   * The conversation you have open is read by definition, so counting it
   * would leave a badge sitting on the header of the very thread you are
   * looking at. What this says is "there is something in another
   * conversation", which is the only useful thing a header badge can say.
   */
  const elsewhere = useMemo(() => {
    const dms = ordered.reduce((n, t) =>
      n + (t.user.username === username ? 0 : (t.unread || 0)), 0);
    const groups = myGroups.reduce((n, g) =>
      n + (g.id === groupId ? 0 : (g.unread || 0)), 0);
    return dms + groups;
  }, [ordered, myGroups, username, groupId]);

  /* The one pinned message in this conversation, and who has read how far. */
  const pinned = shown.find((m) => m.pinned) ?? null;
  const [pinDismissed, setPinDismissed] = useState<string | null>(null);
  // Which picture the gallery is showing, or null while it is closed.
  const [gallery, setGallery] = useState<number | null>(null);
  /* Shared media opened out inside the panel rather than four thumbnails. */
  const [allMedia, setAllMedia] = useState(false);

  /** Everything pinned here. */
  const pins = shown.filter((m) => m.pinned);

  /** Scrolls the log to a message and lights it, the way a reply does. */
  function jumpToMessage(id: string) {
    const node = document.getElementById('msg-' + id);
    if (!node) return;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    node.classList.add('msg-flash');
    setTimeout(() => node.classList.remove('msg-flash'), 1200);
  }

  /** Saves a file from the panel rather than opening it in a browser. */
  function saveAttachment(a: { url: string; name: string }) {
    const link = document.createElement('a');
    link.href = fileUrl(a.url);
    link.download = a.name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }
  const channelId = other && me ? dmChannel(me.id, other.id) : null;

  // Opening a conversation marks it read, and the other side is told so their
  // last message can show as seen.
  useEffect(() => {
    if (!channelId || !messages.length) return;
    const newest = messages[messages.length - 1]!.createdAt;
    void endpoints.markRead(channelId, newest).catch(() => {});
    // Opening the conversation is what clears the badge. It only used to be
    // sent when a message arrived while you were already looking, so a chat
    // you opened with three waiting stayed on three.
    socket.send('read', { channelId });
  }, [channelId, messages.length]);



  useEffect(() => socket.on('unread', (d: { channelId: string; count: number }) => {
    if (d.count !== 0) return;
    void queryClient.invalidateQueries({ queryKey: ['dms'] });
  }), [queryClient]);

  /**
   * Pin, immediately.
   *
   * The pinned bar and the side panel both read off the cache, so writing to
   * it first means the message moves the instant you click - the round trip
   * then either agrees or the change is put back.
   *
   * A group keeps its log somewhere else entirely: under group-log, and mirrored
   * into local state so an arriving message does not refetch the lot. Pinning
   * used to write only to the direct-message cache, which in a group is a key
   * that does not exist - so the pin travelled to the server and came back on
   * the next reload, and in between nothing on screen moved at all.
   */
  async function togglePin(m: Message) {
    const want = !m.pinned;
    // pinned carries who and when, so the optimistic value is the real shape
    // rather than a flag the panel would then have to special-case.
    const mark: Message['pinned'] = want ? { by: me?.id ?? '', at: Date.now() } : undefined;

    patchMessage(queryClient, m.id, (x) => ({ ...x, pinned: mark }));
    try {
      await endpoints.pinMessage(m.id, want);
    } catch {
      patchMessage(queryClient, m.id, (x) => ({ ...x, pinned: m.pinned }));
      toast(want ? 'Could not pin that' : 'Could not unpin that', 'bad');
    }
  }


  /* Announcements, as a thread you can read but not write in. */
  const news = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ announcements: Announcement[] }>('/api/announcements')
  });
  const isBot = username === BOT_USERNAME;
  const bot = botUser();
  const catalog = useSession((st) => st.catalog);
  const otherGame = other?.games?.[0] ?? null;
  const otherGameMeta = catalog?.games.find((g) => g.id === otherGame?.id);


  // Everything shared in this conversation, newest first. Read off the
  // messages already loaded rather than a second request - the log is right
  // here, and a separate endpoint would only be able to say the same thing.
  const shared = messages.flatMap((m) => (m.attachments ?? []).map((a) => ({ a, at: m.createdAt })));
  const media = shared.filter((s) => s.a.image).reverse();
  const files = shared.filter((s) => !s.a.image).reverse();

  return (
    /*
     * Three cards, not three columns.
     *
     * The screen used to be one surface divided by hairlines, which puts the
     * conversation list, the messages and the person all on the same plane -
     * so nothing says which of them you are meant to be reading. Separate
     * panels with air between them give the middle one the weight it should
     * have had all along, and the edges stop the list running into the
     * window.
     */
    <div className="cols-desktop flex min-h-0 flex-1 gap-3 p-3">
      {/* ---- conversations ---- */}
      {/* On a phone this is the whole screen until you pick somebody, and
          then it steps aside - a 268px column beside a conversation leaves
          room for neither. */}
      <ConversationColumn narrow={narrow} hidden={narrow && (!!username || !!groupId)}>
        {/* The column has a name in the reference, and the one thing you
            cannot do from a row - start a new conversation - sits beside it. */}
        {/* The header measures the column, so at a narrow window the button
            keeps its icon and drops its words instead of spilling out. */}
        <div className="@container flex flex-none items-center gap-2 px-1 pt-1 pb-3">
          {/*
            Not on a phone, where the bar above already says Chat.

            Two headings in the top 100px of a 375px screen is one too many,
            and the one that lost the argument lost it by truncating to
            "Messa…" - which looks like a bug rather than a choice. The
            spacer keeps the buttons where they were.
          */}
          <h2 className={cn(
            'min-w-0 flex-1 truncate text-[26px] font-extrabold tracking-[-.025em]',
            narrow && 'sr-only'
          )}>
            Messages
          </h2>
          {narrow ? <span className="min-w-0 flex-1" /> : null}
          <Tooltip content="New group">
            <button
              type="button"
              aria-label="New group"
              onClick={() => setMaking(true)}
              className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 text-mute shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-[background-color,color] duration-[250ms] hover:bg-accent-soft hover:text-accent focus-visible:shadow-[0_0_0_2px_var(--accent)]"
            >
              <Icon name="friends" size={17} />
            </button>
          </Tooltip>
          <button
            type="button"
            onClick={openPalette}
            aria-label="New message"
            className="flex h-10 flex-none items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-3 text-[14.5px] font-bold text-accent-fg shadow-[0_8px_18px_-10px_color-mix(in_srgb,var(--accent)_85%,transparent)] outline-none transition-[background-color,transform] duration-[250ms] hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)] @[320px]:px-4"
          >
            <Icon name="plus" size={16} />
            <span className="hidden @[320px]:inline">New message</span>
          </button>
        </div>

        <div className="flex flex-none items-center gap-2 pb-3">
          <div className="relative min-w-0 flex-1">
            <Icons.Search size={16} className="absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search conversations"
              aria-label="Search conversations"
              spellCheck={false}
              className="h-11 w-full rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 pr-3 pl-10 text-[14.5px] shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-shadow duration-[250ms] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]"
            />
          </div>

        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          {/* One container: Pugo, then every conversation and friend in the
              order they last happened. No online/offline split - a green dot
              on the row already says who is about. */}
          <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto">
            {/* A first message from somebody you have never spoken to waits
                here rather than among your friends. */}
            <MessageRequests compact />

            <BotRow active={isBot} title={news.data?.announcements?.[0]?.title} bot={bot} onOpen={() => go('chat/' + BOT_USERNAME)} />

            {/* Groups above the people. There are only ever a handful and
                they are the threads you go looking for by name. */}
            {myGroups.length ? (
              <>
                <div className="my-1 border-b border-line-soft" />
                {myGroups.map((g) => (
                  <GroupRow
                    key={g.id}
                    group={g}
                    active={groupId === g.id}
                    onOpen={() => go('chat/g/' + g.id)}
                    onLeave={() => { void leave(g).then((left) => { if (left && groupId === g.id) { rememberChat(null); go('chat'); } }); }}
                  />
                ))}
              </>
            ) : null}

            {ordered.length ? (
              <>
                <div className="my-1 border-b border-line-soft" />
                {ordered.map((t) => (
                  <ThreadRow
                    key={t.user.id}
                    thread={t}
                    active={username === t.user.username}
                    mine={me?.id}
                    onOpen={() => { reopenThread(t.user.username); go('chat/' + t.user.username); }}
                    onClose={() => closeThread(t.user.username)}
                  />
                ))}
              </>
            ) : (
              <p className="px-3 py-6 text-center text-[12.5px] text-mute">
                {filter.trim() ? 'Nobody matches that.' : 'Add a friend to start chatting.'}
              </p>
            )}
          </div>
        </div>

      </ConversationColumn>


      {/* ---- the conversation ---- */}
      {narrow && !username && !groupId ? null : (
      // min-h-0 matters: every other link in this column has it, and without
      // it here the section grows to the height of the whole conversation.
      // The log inside then inherits that height instead of being capped, so
      // it has nothing to scroll - which is why a long chat could not be
      // scrolled on a phone at all.
      <section className="smooth-clip flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        {openGroup ? (
          <>
            <GroupHeader
              group={openGroup}
              narrow={narrow}
              panelOpen={detailsOpen}
              onBack={() => go('chat')}
              onTogglePanel={toggleDetails}
            />

            <PinnedBar message={pinned} dismissed={pinDismissed} onDismiss={setPinDismissed} />

            <div className="flex min-h-0 flex-1 flex-col">
              <ChatLog
                messages={groupMessages}
                emptyHint="Say something."
                onReply={setReplyTo}
                onPin={(m) => void togglePin(m)}
              />
            </div>

            <TypingLine match={(p) => p.scope === 'group' && p.channelId === 'gc:' + openGroup.id} />

            <CallBar room={groupRoom(openGroup.id)} people={openGroup.members.map((m) => m.user)} />

            <Composer
              focusKey={openGroup.id}
              poll={{ scope: 'group', channelId: 'gc:' + openGroup.id }}
              people={openGroup.members.map((m) => m.user)}
              placeholder={'Message ' + openGroup.name + '…'}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSend={async (body, attachments, reply, spoilers) => {
                await api.post('/api/messages', {
                  scope: 'group', groupId: openGroup.id, body, attachments, replyTo: reply, spoilers
                });
              }}
              onTyping={() => socket.send('typing', { scope: 'group', groupId: openGroup.id })}
            />
          </>
        ) : isBot ? (
          <>
            <header className="flex flex-none items-center gap-3.5 border-b border-line-soft px-5 py-3.5">
              <UnreadElsewhere count={elsewhere} />
              {narrow ? (
                <button
                  type="button"
                  aria-label="Back to conversations"
                  onClick={() => go('chat')}
                  className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-2 hover:text-ink"
                >
                  <Icon name="chevronL" size={18} />
                </button>
              ) : null}
              <Avatar user={bot} size={34} />
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-[15px] font-bold">Pugo</h1>
                <p className="truncate text-[12px] text-mute">Announcements · read only</p>
              </div>
            </header>
            <BotThread list={news.data?.announcements ?? []} />
            <BotComposer />
          </>
        /*
         * Nothing picked is the empty state. A conversation that is still
         * loading is not - it gets its frame straight away, drawn from the
         * username in the address bar, and fills in. Waiting for the fetch
         * meant the header appeared a beat late and shoved the whole
         * conversation down as it did.
         */
        ) : !username && !groupId ? (
          <NoConversation recent={ordered.slice(0, 3)} onOpen={(u) => go('chat/' + u)} />
        ) : username && !other ? (
          <PendingConversation username={username} narrow={narrow} onBack={() => go('chat')} />
        ) : groupId ? (
          /*
           * A group route with no group yet.
           *
           * This used to fall through to the direct-message branch below,
           * which draws a header for the person you are talking to - and on a
           * group route there is no such person. Rendering an avatar for
           * undefined threw, and the crash is why opening a group showed
           * nothing at all. Now the wait is a wait, and a failure says so.
           */
          <div className="grid flex-1 place-items-center px-6">
            {groupLog.isError ? (
              <div className="text-center">
                <p className="text-[14px] font-semibold">This group could not be opened.</p>
                <p className="mt-1 text-[12.5px] text-mute">
                  {(groupLog.error as Error | undefined)?.message ?? 'It may have been deleted, or you were removed from it.'}
                </p>
                <button
                  type="button"
                  onClick={() => go('chat')}
                  className="mt-3 text-[12.5px] font-semibold text-accent-hi hover:underline"
                >
                  Back to conversations
                </button>
              </div>
            ) : (
              <p className="text-[13px] text-mute">Opening…</p>
            )}
          </div>
        ) : other ? (
          <>
            <header className="flex flex-none items-center gap-3.5 border-b border-line-soft px-5 py-3.5">
              <UnreadElsewhere count={elsewhere} />
              {/* The way back to the list, which is the whole screen here. */}
              {narrow ? (
                <button
                  type="button"
                  aria-label="Back to conversations"
                  onClick={() => go('chat')}
                  className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-2 hover:text-ink"
                >
                  <Icon name="chevronL" size={18} />
                </button>
              ) : null}
              <button
                type="button"
                aria-label={'Show ' + other.displayName + "'s profile card"}
                onClick={(e) => { e.stopPropagation(); openUserCard(other, e.currentTarget); }}
                className="flex-none rounded-full outline-none transition-transform duration-[250ms] hover:scale-[1.03] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              >
                <Avatar user={other} size={52} presence />
              </button>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-[21px] font-bold tracking-[-.015em]"><UserName user={other} profile={false} /></h1>
                {/* Every status, not just two - "Online" for somebody on Do not
                    disturb invites exactly the message they asked not to get. */}
                <p className="flex items-center gap-1.5 truncate text-[13px] text-mute">
                  <span
                    aria-hidden
                    className={cn(
                      'h-2 w-2 flex-none rounded-full',
                      other.status === 'dnd' ? 'bg-bad' : other.status === 'idle' ? 'bg-warn'
                        : other.status === 'offline' || other.status === 'invisible' ? 'bg-ghost' : 'bg-good'
                    )}
                  />
                  {other.status === 'dnd' ? 'Do not disturb'
                    : other.status === 'idle' ? 'Away'
                      : other.status === 'offline' || other.status === 'invisible' ? 'Offline' : 'Online'}
                </p>
              </div>

              {channelId ? <ChatTools channelId={channelId} name={other.displayName} /> : null}
              <CallButton
                room={me ? dmRoom(me.id, other.id) : null}
                label={'Call ' + other.displayName}
              />
              <HeadBtn label="View profile" icon="profile" onClick={() => go('u/' + other.username)} />
              {narrow ? null : (
                <HeadBtn
                  label={detailsOpen ? 'Hide details' : 'Show details'}
                  icon="info"
                  on={detailsOpen}
                  onClick={toggleDetails}
                />
              )}
            </header>

            <PinnedBar message={pinned} dismissed={pinDismissed} onDismiss={setPinDismissed} />

            {/* Keyed on the conversation so switching cross-fades. Without a
                key React reuses the same nodes and the messages simply swap
                under you, which is what reads as flicking when you click
                between chats quickly. */}
            <div key={username} className="cross-in flex min-h-0 flex-1 flex-col">
              {loading && !messages.length ? (
                <MessageSkeleton />
              ) : (
                <ChatLog
                  pair
                  messages={messages}
                  onReply={setReplyTo}
                  onPin={(m) => void togglePin(m)}
                  onLoadOlder={() => void loadOlder()}
                  hasOlder={hasOlder}
                  loadingOlder={loadingOlder}
                  emptyHint="Say something."
                />
              )}
            </div>

            <TypingLine match={(p) => p.scope === 'dm' && p.user.id === other.id} />

            <CallBar room={me ? dmRoom(me.id, other.id) : null} people={[other]} />

            <Composer
              focusKey={other.username}
              poll={channelId ? { scope: 'dm', channelId } : undefined}
              people={[other]}
              placeholder={'Message ' + other.displayName + '…'}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              onSend={async (body, attachments, reply, spoilers) => {
                await api.post('/api/messages', { scope: 'dm', username: other.username, body, attachments, replyTo: reply, spoilers });
              }}
              onTyping={() => socket.send('typing', { scope: 'dm', userId: other.id })}
            />
          </>
        ) : (
          /* No group, no person - the empty state of the chat screen. */
          <div className="grid flex-1 place-items-center px-6">
            <p className="text-[13px] text-mute">Pick a conversation.</p>
          </div>
        )}
      </section>
      )}

      {/* The roster, where a DM shows the person. */}
      {openGroup && !narrow ? (
        <DetailsPanel inline={detailsInline} open={detailsOpen} onClose={() => setDrawerOpen(false)} label="Group details">
          <GroupPanel
            group={openGroup}
            onLeave={() => { void leave(openGroup).then((left) => { if (left) { rememberChat(null); go('chat'); } }); }}
          />
        </DetailsPanel>
      ) : null}

      {leaveDialog}
      {making ? (
        <NewGroup
          onClose={() => setMaking(false)}
          onMade={(id) => { setMaking(false); void queryClient.invalidateQueries({ queryKey: ['groups'] }); go('chat/g/' + id); }}
        />
      ) : null}

      {/* ---- who you are talking to, and what you have shared ---- */}
      {other && !narrow ? (
        <DetailsPanel inline={detailsInline} open={detailsOpen} onClose={() => setDrawerOpen(false)} label={'About ' + other.displayName} scroll={false}>
          {/*
            * Their banner, the way their profile has it.
            *
            * The panel opened on a picture floating in a blank column, which
            * is the one place in the app where somebody's own colours were
            * thrown away. The banner sits behind the top of the card and the
            * picture overlaps it, so the ring reads against the artwork
            * instead of against nothing.
            */}
          <div className="-mx-4 -mt-4 overflow-hidden rounded-t-[calc(22px*var(--radius-mul,1))]">
            <div
              className="relative h-[150px]"
              style={{
                background: other.banner
                  ? 'var(--bg-2)'
                  : other.bannerColor
                    ? 'linear-gradient(118deg, ' + other.bannerColor + ', color-mix(in srgb, ' + other.bannerColor + ' 34%, transparent)), var(--bg-2)'
                    : 'linear-gradient(118deg, color-mix(in srgb, var(--accent) 30%, transparent), transparent 68%), var(--bg-2)'
              }}
            >
              {other.banner ? (
                <img src={absolute(other.banner)} alt="" className="h-full w-full object-cover" />
              ) : null}
              {/* Solid at the bottom, so the name below never fights the art. */}
              <span
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-[70%]"
                style={{
                  background:
                    'linear-gradient(to top, var(--bg-1) 0%, var(--bg-1) 30%,' +
                    ' color-mix(in srgb, var(--bg-1) 76%, transparent) 58%, transparent 100%)'
                }}
              />
            </div>
          </div>

          <div className="-mt-[74px] flex flex-col items-center gap-1 text-center">
            <button
              type="button"
              aria-label={'Show ' + other.displayName + "'s profile card"}
              onClick={(e) => openUserCard(other, e.currentTarget)}
              className="rounded-full p-[4px] outline-none transition-transform duration-[300ms] ease-[var(--ease-out)] hover:scale-[1.03] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
              style={{ background: 'var(--bg-1)' }}
            >
              <FramedAvatar user={other} size={124} presence />
            </button>
            <UserName user={other} profile={false} className="mt-1 block max-w-full truncate text-[25px] font-bold tracking-[-.02em]" />
            <p className="m-0 line-clamp-2 px-3 text-[14.5px] leading-snug text-mute">
              {other.customStatus
                ? <EmojiText text={other.customStatus} size={15} />
                : other.status === 'offline' || other.status === 'invisible' ? 'Offline' : 'Online'}
            </p>
          </div>

          {/* The row of round actions under the name, as the reference has it. */}
          <PanelActions user={other} room={me ? dmRoom(me.id, other.id) : null} />

          {/*
            Everything from here down scrolls; the banner, the picture, the
            name and the actions above it stay where they are. Opening Shared
            media therefore grows inside this area rather than pushing the
            person off the top of the panel.
          */}
          <div className="-mr-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-2">
          {other.bio ? (
            <PanelBlock label="About">
              <p className="text-[15px] leading-relaxed text-dim"><EmojiText text={other.bio} size={16} /></p>
            </PanelBlock>
          ) : null}

          {other.games?.length ? (
            <PanelBlock label="Games" onSeeAll={() => go('u/' + other.username)}>
              <div className="grid grid-cols-3 gap-2">
                {other.games.slice(0, 3).map((g, i) => {
                  const meta = catalog?.games.find((x) => x.id === g.id);
                  const art = gameIconArt(g.id);
                  return (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => go('parties?game=' + encodeURIComponent(g.id))}
                      style={{ animationDelay: i * 50 + 'ms' }}
                      className="flex flex-col items-center gap-1.5 rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 px-1.5 pt-2.5 pb-2 text-center shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none animate-[fade-in_280ms_ease-out_backwards] transition-[transform,box-shadow] duration-[250ms] hover:-translate-y-0.5 hover:shadow-[inset_0_0_0_1px_var(--line),var(--shadow-1)] focus-visible:shadow-[0_0_0_2px_var(--accent)] motion-reduce:animate-none"
                    >
                      <span className="smooth-clip grid h-14 w-14 place-items-center overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] bg-surface-3">
                        {art ? <img src={art} alt="" className="h-full w-full object-cover" /> : <Icon name={meta?.icon ?? 'gamepad'} size={22} />}
                      </span>
                      <span className="w-full truncate text-[13.5px] font-bold text-ink">{meta?.name ?? g.id}</span>
                      <span className="w-full truncate text-[12px] text-mute">{g.rank || meta?.genre || 'Plays this'}</span>
                    </button>
                  );
                })}
              </div>
            </PanelBlock>
          ) : null}

          {media.length ? (
            <PanelBlock
              label="Shared media"
              seeAllLabel={allMedia ? 'Show less' : 'See all'}
              onSeeAll={media.length > 4 ? () => setAllMedia(!allMedia) : undefined}
            >
              <div className="grid grid-cols-4 gap-1.5">
                {media.slice(0, allMedia ? media.length : 4).map(({ a }, i) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setGallery(i)}
                    aria-label={'Open ' + a.name}
                    className="smooth-clip aspect-square overflow-hidden rounded-[calc(10px*var(--radius-mul,1))] bg-surface-3 outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                  >
                    <img src={fileUrl(a.url)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[350ms] hover:scale-[1.08]" />
                  </button>
                ))}
              </div>
            </PanelBlock>
          ) : null}

          {pins.length ? (
            <PanelBlock label="Pinned messages" icon="pin">
              <div className="flex flex-col gap-2">
                {pins.slice(0, 3).map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => jumpToMessage(m.id)}
                    className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 p-3 text-left shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
                  >
                    <span className="flex items-center gap-2">
                      {m.user ? <Avatar user={m.user} size={24} /> : null}
                      <span className="truncate text-[13.5px] font-bold text-ink">{m.user?.displayName ?? 'Someone'}</span>
                      <span className="flex-none text-[12px] text-mute">{new Date(m.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                    </span>
                    <span className="mt-1.5 block line-clamp-2 text-[14px] text-dim">{m.body || 'attachment'}</span>
                  </button>
                ))}
              </div>
            </PanelBlock>
          ) : null}

          {otherGame ? (
            <PanelBlock label="Playing">
              <div className="flex items-center gap-3 rounded-[var(--radius-l)] border border-line-soft bg-surface-2 p-3">
                <span
                  className="grid h-11 w-11 flex-none place-items-center overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] border"
                  style={{
                    borderColor: 'color-mix(in srgb, ' + (otherGameMeta?.color ?? 'var(--accent)') + ' 38%, transparent)',
                    background: 'color-mix(in srgb, var(--bg-0) 60%, transparent)',
                    color: otherGameMeta?.color ?? 'var(--accent)'
                  }}
                >
                  {gameIconArt(otherGame.id)
                    ? <img src={gameIconArt(otherGame.id)!} alt="" className="h-full w-full object-cover" />
                    : <Icon name={otherGameMeta?.icon ?? 'dice'} size={18} />}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-bold">{otherGameMeta?.name ?? otherGame.id}</div>
                  <div className="flex items-center gap-1.5 text-[11.5px] text-mute">
                    <span className="h-[6px] w-[6px] flex-none rounded-full bg-good" />
                    {otherGame.rank || 'In game'}
                  </div>
                </div>
              </div>
            </PanelBlock>
          ) : null}

          <PanelFold
            label="Files"
            count={files.length}
            icon="download"
            empty="No files yet."
          >
            <div className="flex flex-col gap-1.5">
              {files.slice(0, 8).map(({ a }) => (
                <div
                  key={a.id}
                  className="flex items-center gap-2.5 rounded-[var(--radius)] border border-line-soft bg-surface-1 p-2"
                >
                  <span className="grid h-8 w-8 flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] bg-surface-3 text-mute">
                    <Icon name="download" size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-semibold">{a.name}</span>
                    <span className="block text-[11px] text-mute">{kb(a.size)}</span>
                  </span>
                  <IconButton label="Save" className="h-7 w-7" onClick={() => saveAttachment(a)}>
                    <Icon name="download" size={13} />
                  </IconButton>
                </div>
              ))}
            </div>
          </PanelFold>
          </div>
        </DetailsPanel>
      ) : null}

      {/* Every picture in the conversation, opened from the panel. */}
      {gallery !== null ? (
        <Lightbox
          list={media.map((m) => m.a)}
          index={gallery}
          onClose={() => setGallery(null)}
        />
      ) : null}
    </div>

  );
}

/** A heading in the conversation list. */
/**
 * The screen before you have picked anybody.
 *
 * Two lines of grey text in the middle of a very large empty rectangle read
 * as something having failed to load. This says the same thing but gives you
 * somewhere to click while it does - the people you were last talking to,
 * which is almost always who you came here for.
 */
/**
 * The conversation, before it has arrived.
 *
 * Exactly the same frame the real one uses - same header height, same
 * composer - so when the messages land nothing moves. The alternative was
 * showing the empty state and then swapping in a header, which shoved the
 * whole screen down a beat after you tapped.
 */
function PendingConversation({ username, narrow, onBack }: {
  username: string;
  narrow: boolean;
  onBack: () => void;
}) {
  return (
    <>
      <header className="flex flex-none items-center gap-3.5 border-b border-line-soft px-5 py-3.5">
        {narrow ? (
          <button
            type="button"
            aria-label="Back to conversations"
            onClick={onBack}
            className="grid h-9 w-9 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] text-mute transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <Icon name="chevronL" size={18} />
          </button>
        ) : null}

        {/* A blank of the right size rather than a spinner. The name is
            already known from the address, so only the picture is missing. */}
        <span className="h-[34px] w-[34px] flex-none animate-pulse rounded-[var(--avatar-radius,50%)] bg-surface-3" />

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[15px] font-bold">{username}</h1>
          <p className="truncate text-[12px] text-mute">Opening…</p>
        </div>
      </header>

      <div className="min-h-0 flex-1" />

      <Composer disabled placeholder={'Message ' + username + '…'} onSend={async () => {}} />
    </>
  );
}

function NoConversation({ recent, onOpen }: {
  recent: Thread[];
  onOpen: (username: string) => void;
}) {
  return (
    <div className="grid flex-1 place-items-center p-6">
      <div className="flex w-full max-w-[380px] flex-col items-center text-center">
        {/* A little conversation list, drawn rather than described. Fades
            down the stack so the eye starts at the top and then goes to the
            heading. Decorative - it is a picture of the thing, not a
            control. */}
        <div aria-hidden className="mb-6 flex w-[168px] flex-col gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="flex items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-1 px-2 py-[7px]"
              style={{ opacity: 1 - i * 0.3 }}
            >
              <span
                className="block h-[18px] w-[18px] flex-none rounded-full"
                style={{ background: i === 0 ? 'var(--accent)' : 'var(--bg-3)' }}
              />
              <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="block h-[4px] rounded-full bg-surface-3" style={{ width: [58, 44, 50][i] + '%' }} />
                <span className="block h-[4px] rounded-full bg-surface-3" style={{ width: [82, 68, 74][i] + '%', opacity: 0.55 }} />
              </span>
            </span>
          ))}
        </div>
        <h2 className="text-[17px] font-bold">Pick a conversation</h2>
        <p className="mt-1 text-[13px] leading-relaxed text-mute">
          Choose someone from the list to start talking.
        </p>

        {recent.length ? (
          <>
            <div className="mt-6 mb-2.5 flex w-full items-center gap-2.5">
              <span className="h-px flex-1 bg-line-soft" />
              <span className="text-[10px] font-bold tracking-[.09em] text-ghost uppercase">
                Jump back in
              </span>
              <span className="h-px flex-1 bg-line-soft" />
            </div>

            <div className="flex w-full flex-col gap-1.5">
              {recent.map((t) => (
                <button
                  key={t.user.id}
                  type="button"
                  onClick={() => onOpen(t.user.username)}
                  className="flex items-center gap-2.5 rounded-[var(--radius-l)] border border-line-soft bg-surface-1 p-2 text-left transition-colors hover:border-accent-line hover:bg-surface-2"
                >
                  <Avatar user={t.user} size={32} presence />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">
                      {t.user.displayName}
                    </span>
                    <span className="block truncate text-[11.5px] text-mute">
                      {t.last?.body || 'No messages yet'}
                    </span>
                  </span>
                  <Icon name="chevronR" size={14} className="flex-none text-ghost" />
                </button>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The conversation list, stacked under the profile card.
 *
 * On a desktop it goes into the sidebar`s slot, so you read down one column
 * - who you are, then who you are talking to - instead of across two that
 * happen to be adjacent. On a phone there is no sidebar and it is the whole
 * screen, so it renders where it stands.
 *
 * The slot is a real DOM node owned by the sidebar, which does not exist on
 * the first paint of a fresh load. Hence the state rather than a ref: when
 * the effect finds it, the portal renders.
 */
function ConversationColumn({ narrow, hidden, children }: {
  narrow: boolean;
  hidden: boolean;
  children: React.ReactNode;
}) {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (narrow) { setSlot(null); return; }
    setSlot(document.getElementById('side-slot'));
  }, [narrow]);

  if (hidden) return null;

  if (slot) {
    return createPortal(
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>,
      slot
    );
  }

  return (
    <aside className={cn(
      /* A panel of its own rather than a column ruled off from the rest.
         Wider than it was: at 268px a name, a time and a preview line all
         competed for the same row and every one of them truncated. */
      'flex flex-col pugo-panel bg-surface-1 animate-[fade-in_260ms_ease-out] motion-reduce:animate-none',
      /*
       * On a phone it is the screen, not a card on it.
       *
       * flex-none with a rounded card left the conversation list floating in
       * the top third with two thirds of black underneath - which reads as
       * the screen having failed to load rather than as a design. A phone
       * has one column; the list IS that column, so it fills it and scrolls
       * inside itself.
       */
      narrow
        ? 'min-h-0 w-full flex-1 rounded-none p-2 shadow-none'
        : 'w-[290px] flex-none rounded-[calc(22px*var(--radius-mul,1))] p-3 shadow-[inset_0_0_0_1px_var(--line-soft)] min-[1280px]:w-[350px]'
    )}>
      {children}
    </aside>
  );
}

/**
 * The Pugo row.
 *
 * Exported because the profile card reuses the exact shape for its own
 * button - two things that sit inches apart in the same column should not be
 * drawn twice and drift.
 */
export function BotRow({ active, title, bot, onOpen }: {
  active: boolean;
  title?: string;
  bot: PublicUser;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex w-full items-center gap-3.5 rounded-[calc(16px*var(--radius-mul,1))] px-3 py-3 text-left outline-none transition-[background-color,box-shadow] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        active ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--accent)_80%,transparent)]' : 'hover:bg-surface-2 hover:shadow-[inset_0_0_0_1px_var(--line-soft)]'
      )}
    >
      <Avatar user={bot} size={44} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[14.5px] font-bold">Pugo</span>
          <span className="flex-none rounded bg-accent-soft px-1.5 text-[9.5px] font-extrabold tracking-[.06em] text-accent-hi">
            BOT
          </span>
        </span>
        <span className="block truncate text-[12.5px] text-mute">{title ?? 'Announcements'}</span>
      </span>
    </button>
  );
}


/** A labelled block in the details panel. */
/**
 * Call, profile and everything else, as round buttons with words under them.
 *
 * Its own component because the player menu is a hook, and the panel only
 * exists when there is somebody to show.
 */
function PanelActions({ user, room }: { user: PublicUser; room: string | null }) {
  const menu = useUserMenu(user);
  const [inviting, setInviting] = useState(false);
  const tile = 'flex w-[70px] flex-col items-center gap-1.5 text-[13px] font-medium text-dim';
  const round = 'grid h-12 w-12 place-items-center rounded-[calc(14px*var(--radius-mul,1))] bg-surface-2 text-ink shadow-[inset_0_0_0_1px_var(--line-soft)] outline-none transition-[background-color,box-shadow,transform] duration-[250ms] hover:bg-surface-3 hover:shadow-[inset_0_0_0_1px_var(--line)] active:scale-95 focus-visible:shadow-[0_0_0_2px_var(--accent)]';
  return (
    <div className="flex justify-center gap-1.5 border-b border-line-soft pb-4">
      <span className={tile}>
        <CallButton room={room} label={'Call ' + user.displayName} />
        Call
      </span>
      <span className={tile}>
        <button type="button" aria-label={'View ' + user.displayName + "'s profile"} className={round} onClick={() => go('u/' + user.username)}>
          <Icon name="profile" size={19} />
        </button>
        Profile
      </span>
      <span className={tile}>
        <button type="button" aria-label={'Invite ' + user.displayName + ' to a party'} className={round} onClick={() => setInviting(true)}>
          <Icon name="gamepad" size={20} />
        </button>
        Invite
        {inviting ? <InviteToParty user={user} onClose={() => setInviting(false)} /> : null}
      </span>
      <span className={tile}>
        <button
          type="button"
          aria-label={'More options for ' + user.displayName}
          aria-haspopup="menu"
          className={round}
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); menu.openAt(Math.max(8, r.right - 214), r.bottom + 4); }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>
        </button>
        More
      </span>
      {menu.menu}
    </div>
  );
}

function PanelBlock({ label, children, onSeeAll, icon, seeAllLabel }: { label: string; children: React.ReactNode; onSeeAll?: () => void; icon?: string; seeAllLabel?: string }) {
  return (
    <section className="border-b border-line-soft pb-4 last:border-b-0 animate-[fade-in_300ms_ease-out] motion-reduce:animate-none">
      <div className="mb-2.5 flex items-center gap-2">
        {icon ? <Icon name={icon} size={16} className="text-ink" /> : null}
        <h3 className="m-0 flex-1 text-[16px] font-bold text-ink">{label}</h3>
        {onSeeAll ? (
          <button type="button" onClick={onSeeAll} className="rounded-[6px] px-1 text-[14px] font-medium text-accent outline-none hover:underline focus-visible:shadow-[0_0_0_2px_var(--accent)]">{seeAllLabel ?? 'See all'}</button>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/** One control in the conversation header. */
function HeadBtn({ label, icon, on, onClick }: {
  label: string;
  icon: string;
  on?: boolean;
  onClick: () => void;
}) {
  return (
    <Tip text={label}>
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className={cn(
          'grid h-11 w-11 flex-none place-items-center rounded-full outline-none transition-[background-color,color,box-shadow,transform] duration-[250ms] active:scale-95 focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          on
            ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1px_var(--accent-line)]'
            : 'text-dim shadow-[inset_0_0_0_1px_var(--line-soft)] hover:bg-surface-2 hover:text-ink'
        )}
      >
        <Icon name={icon} size={17} />
      </button>
    </Tip>
  );
}

/** "1.1 MB". Short, because the row has one line for it. */
function kb(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function ThreadRow({ thread, active, mine, onOpen, onClose }: {
  thread: Thread;
  active: boolean;
  mine?: string;
  onOpen: () => void;
  onClose: () => void;
}) {
  const menu = useUserMenu(thread.user);

  return (
    <div
      role="button"
      tabIndex={0}
      {...menu.props}
      onContextMenu={(e) => {
        // The shared menu first, then the one action that only makes sense here.
        menu.props.onContextMenu(e);
      }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      className={cn(
        /* Outlined rather than shaded when it is the one you are in. A grey
           fill on a grey panel is a difference you have to look for; a lit
           edge is one you see. */
        /* A soft filled row for the conversation you are in, as the
           reference draws it. */
        'group flex w-full cursor-pointer items-center gap-3.5 rounded-[calc(16px*var(--radius-mul,1))] px-3 py-3 text-left outline-none',
        'transition-[background-color,box-shadow] duration-[250ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        /* The one you are talking to: filled and outlined in your accent. */
        active
          ? 'bg-accent-soft shadow-[inset_0_0_0_1.5px_color-mix(in_srgb,var(--accent)_80%,transparent)]'
          : 'hover:bg-surface-2 hover:shadow-[inset_0_0_0_1px_var(--line-soft)]'
      )}
    >
      <Avatar user={thread.user} size={52} presence />
      <span className="min-w-0 flex-1">
        <UserName user={thread.user} profile={false} className="text-[16px] font-bold" />
        <span className="mt-0.5 block truncate text-[14px] text-mute">
          {thread.last
            ? (thread.last.userId === mine ? 'You: ' : '') + (thread.last.body || 'attachment')
            : thread.user.status === 'offline' ? 'Offline' : 'Online'}
        </span>
      </span>

      {/* When it happened, so a quiet conversation is obviously quiet. */}
      {thread.last ? (
        <span className="flex-none self-start pt-0.5 text-[13px] text-mute tabular-nums">
          {shortWhen(thread.last.createdAt)}
        </span>
      ) : null}

      {thread.unread ? (
        <span className="grid h-6 min-w-6 flex-none place-items-center self-end rounded-[calc(8px*var(--radius-mul,1))] bg-accent px-1.5 text-[12.5px] font-bold text-accent-fg tabular-nums">
          {thread.unread > 9 ? '9+' : thread.unread}
        </span>
      ) : (
        <button
          type="button"
          title="Close conversation"
          // Stopped at pointer-down as well as click. The row opens on click,
          // and stopping only the click still let the press through - so
          // closing a conversation immediately reopened it.
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          className="grid h-6 w-6 flex-none place-items-center rounded-[calc(8px*var(--radius-mul,1))] text-mute opacity-0 transition-[opacity,background,color] group-hover:opacity-100 hover:bg-surface-4 hover:text-ink"
        >
          <Icons.Close size={12} />
        </button>
      )}
      {menu.menu}
    </div>
  );
}

/** Placeholders shaped like messages, so the log does not jump when it lands. */
function MessageSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-end gap-4 p-4">
      {[68, 44, 92, 56].map((w, i) => (
        <div key={i} className="flex gap-3">
          <div className="h-9 w-9 flex-none animate-pulse rounded-[30%] bg-surface-2" />
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="h-3 w-24 animate-pulse rounded bg-surface-2" />
            <div className="h-4 animate-pulse rounded bg-surface-2" style={{ width: w + '%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
/**
 * The channel id for a conversation between two people.
 *
 * Sorted, so both sides compute the same string - the server does exactly
 * this, and the two have to agree or read markers land in a channel nobody
 * is looking at.
 */
function dmChannel(a: string, b: string): string {
  return 'dm:' + [a, b].sort().join('|');
}
/** "2m", "3h", "Tue". Short, because the row has one line for it. */
function shortWhen(at: number): string {
  const s = Math.max(0, (Date.now() - at) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return Math.floor(s / 60) + 'm';
  if (s < 86400) return Math.floor(s / 3600) + 'h';
  if (s < 7 * 86400) return new Date(at).toLocaleDateString([], { weekday: 'short' });
  return new Date(at).toLocaleDateString([], { day: 'numeric', month: 'short' });
}
/**
 * The one pinned message, across the top of the conversation.
 *
 * Written once and used by both kinds of conversation. It only lived in the
 * direct-message branch before, so pinning in a group put the pin somewhere
 * with nothing to show it - the action worked and looked as though it had
 * not.
 */
function PinnedBar({ message, dismissed, onDismiss }: {
  message: Message | null;
  dismissed: string | null;
  onDismiss: (id: string) => void;
}) {
  if (!message || dismissed === message.id) return null;

  return (
    <div className="flex flex-none items-center gap-2.5 border-b border-line-soft bg-surface-1 px-4 py-2">
      <Icon name="pin" size={15} className="flex-none text-accent-hi" />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] text-mute">
          Pinned by {message.user?.displayName ?? 'someone'}
        </div>
        <div className="truncate text-[12.5px] text-dim">{message.body}</div>
      </div>
      <IconButton label="Dismiss" onClick={() => onDismiss(message.id)}>
        <Icons.Close size={13} />
      </IconButton>
    </div>
  );
}

/**
 * "There is something in another conversation."
 *
 * A dot when you are on a phone, where the list is a screen away and the
 * number would not fit; the number itself on a desktop, where the list is
 * right there and the count tells you whether it is worth looking.
 *
 * Nothing at all when there is nothing - an always-present zero is furniture.
 */
function UnreadElsewhere({ count }: { count: number }) {
  if (!count) return null;

  return (
    <button
      type="button"
      onClick={() => go('chat')}
      title={count + ' unread in other conversations'}
      aria-label={count + ' unread in other conversations'}
      className="flex flex-none items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--bad)_16%,transparent)] py-1 pr-2.5 pl-2 text-bad transition-[background-color] hover:bg-[color-mix(in_srgb,var(--bad)_84%,#fff)]"
    >
      <span className="block h-[7px] w-[7px] rounded-full bg-bad" />
      <span className="text-[11.5px] font-extrabold tabular-nums">
        {count > 99 ? '99+' : count}
      </span>
    </button>
  );
}

/**
 * Details for the open conversation: inline when there is room, a drawer when
 * there is not.
 *
 * The drawer is a real dialog - focus moves into it, Escape and the scrim close
 * it, and focus goes back to whatever opened it. Nothing behind it moves, so
 * opening it never reflows the messages you were reading.
 */
function DetailsPanel({ inline, open, onClose, label, children, scroll = true }: {
  inline: boolean;
  open: boolean;
  onClose: () => void;
  label: string;
  children: React.ReactNode;
  /** Off when the panel scrolls a part of itself and keeps the rest still. */
  scroll?: boolean;
}) {
  const panel = useRef<HTMLElement | null>(null);
  const split = useSplitter({ key: 'pogu.chat.details', initial: 340, min: 280, max: 560, side: 'left' });

  useEffect(() => {
    if (inline || !open) return;
    const back = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      back?.focus?.();
    };
  }, [inline, open, onClose]);

  if (!open) return null;

  if (inline) {
    return (
      <>
      {/* Drag the panel narrower and the conversation grows into the room. */}
      <Grip dragging={split.dragging} label="Resize the details panel" {...split.gripProps} />
      <aside
        ref={split.panelRef as React.RefObject<HTMLElement>}
        style={{ width: split.width }}
        aria-label={label}
        className={cn(
          'panel-right aside-desktop flex flex-none flex-col gap-4 pugo-panel rounded-[calc(22px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]',
          scroll ? 'overflow-y-auto' : 'overflow-hidden',
          split.dragging ? 'transition-none' : 'transition-[width] duration-[250ms] ease-[var(--ease-out)] motion-reduce:transition-none'
        )}
      >
        {children}
      </aside>
      </>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[420]">
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(24,18,10,.26)] animate-[scrim-in_var(--t-panel-in)_var(--ease)]"
      />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={cn(
          'absolute top-3 right-3 bottom-3 flex w-[min(360px,calc(100vw-24px))] flex-col gap-4 overscroll-contain',
          scroll ? 'overflow-y-auto' : 'overflow-hidden',
          'rounded-[var(--radius-xl)] border border-line bg-surface-1 p-4 shadow-[var(--shadow-3)] outline-none',
          'animate-[drawer-in_var(--t-panel-in)_var(--ease-out)]'
        )}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="absolute top-3 right-3 z-[2] grid h-9 w-9 place-items-center rounded-[calc(10px*var(--radius-mul,1))] border border-line-soft bg-surface-1/90 text-mute transition-colors duration-[var(--t-hover)] ease-[var(--ease)] hover:border-line hover:text-ink"
        >
          <Icons.Close size={15} />
        </button>
        {children}
      </aside>
    </div>,
    document.body
  );
}
